import { Injectable, signal } from '@angular/core';
import { defer, delay, Observable, of } from 'rxjs';

import type {
  FeatureComparisonClient,
  TableDataResponse,
} from '../../../core/tokens/feature-comparison-client.token';
import type { TableQuery } from '../../../shared/components/generic-table/models/table-query.model';
import type { LineChartConfig, LineSeries } from '../../../shared/echarts/models/chart-types';
import type { ComparisonTableRow } from '../models/comparison-table-row.model';

/** Re-export for consumers that need the type. */
export type { TableDataResponse } from '../../../core/tokens/feature-comparison-client.token';

export type SelectOption = { label: string; value: string };

/** Legend/line colors per feature: turquoise, purple, gold, blue to match design */
const FEATURE_LINE_COLORS = ['#22D3EE', '#9333EA', '#F59E0B', '#3B82F6'] as const;

const X_AXIS_CATEGORIES = ['D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'D8', 'D9', 'D10', 'D11', 'D12', 'D13', 'D14'];

/** Format date as MM/DD/YY for chart axis. */
function formatDateMMDDYY(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const y = String(d.getFullYear()).slice(-2);
  return `${m}/${day}/${y}`;
}

/** Generate n dates evenly spanning [start, end] (for tooltip display). */
function dateLabelsForRange(start: Date, end: Date, n: number): string[] {
  if (n <= 0) return [];
  const s = start.getTime();
  const e = end.getTime();
  const step = n === 1 ? 0 : (e - s) / (n - 1);
  return Array.from({ length: n }, (_, i) =>
    formatDateMMDDYY(new Date(s + step * i))
  );
}

@Injectable({
  providedIn: 'root',
})
export class FeatureComparisonMockService implements FeatureComparisonClient {
  private readonly comparisonTableRows: readonly ComparisonTableRow[] =
    this.generateComparisonTableRows(80);

  readonly featureFilters = signal<SelectOption[]>([
    { label: 'Feature 1', value: 'feature1' },
    { label: 'Feature 2', value: 'feature2' },
    { label: 'Feature 3', value: 'feature3' },
  ]);

  readonly comparisonTimePeriodOptions = signal<SelectOption[]>([
    { label: 'Last 30 Days', value: 'last30Days' },
    { label: 'Last 60 Days', value: 'last60Days' },
    { label: 'Last 90 Days', value: 'last90Days' },
  ]);

  readonly comparisonAuditOptions = signal<SelectOption[]>([
    { label: 'Unreconciled Meds', value: 'unreconciledMeds' },
    { label: 'Audit 1', value: 'audit1' },
    { label: 'Audit 2', value: 'audit2' },
  ]);

  readonly comparisonMethodOptions = signal<SelectOption[]>([
    { label: 'Total Count', value: 'totalCount' },
    { label: 'Method 2', value: 'method2' },
    { label: 'Method 3', value: 'method3' },
  ]);

  readonly comparisonFavoritesOptions = signal<SelectOption[]>([
    { label: 'No favorites saved', value: '' },
    { label: 'Last 90 Days / Unreconciled / Total', value: 'fav1' },
    { label: 'Last 30 Days / Audit 1 / Method 2', value: 'fav2' },
  ]);

  readonly filterFavoritesOptions = signal<SelectOption[]>([
    { label: 'No favorites saved', value: '' },
    { label: 'Default feature filters', value: 'default' },
  ]);

  readonly medicationOptions = signal<SelectOption[]>([
    { label: 'Oxycodone', value: 'Oxycodone' },
    { label: 'Morphine', value: 'Morphine' },
    { label: 'Fentanyl', value: 'Fentanyl' },
  ]);
  readonly clinicalUnitOptions = signal<SelectOption[]>([
    { label: 'Unit A', value: 'unitA' },
    { label: 'Unit B', value: 'unitB' },
  ]);
  readonly serviceDeptOptions = signal<SelectOption[]>([
    { label: 'Pharmacy', value: 'pharmacy' },
    { label: 'Nursing', value: 'nursing' },
  ]);
  readonly patientNameOptions = signal<SelectOption[]>([
    { label: 'Patient 1', value: 'patient1' },
    { label: 'Patient 2', value: 'patient2' },
  ]);
  readonly patientIdOptions = signal<SelectOption[]>([
    { label: 'ID-001', value: 'ID-001' },
    { label: 'ID-002', value: 'ID-002' },
  ]);
  readonly employeeOptions = signal<SelectOption[]>([
    { label: 'Chuck Harrison', value: 'Chuck Harrison' },
    { label: 'Jane Doe', value: 'Jane Doe' },
  ]);
  readonly employeeIdOptions = signal<SelectOption[]>([
    { label: 'EMP-001', value: 'EMP-001' },
    { label: 'EMP-002', value: 'EMP-002' },
  ]);
  readonly employeeJobRoleOptions = signal<SelectOption[]>([
    { label: 'Pharmacist', value: 'pharmacist' },
    { label: 'Nurse', value: 'nurse' },
  ]);
  readonly shiftOptions = signal<SelectOption[]>([
    { label: 'Day', value: 'day' },
    { label: 'Night', value: 'night' },
  ]);
  readonly fteStatusOptions = signal<SelectOption[]>([
    { label: 'Full-time', value: 'fullTime' },
    { label: 'Part-time', value: 'partTime' },
  ]);

  getComparisonTableRows(
    query: TableQuery
  ): Observable<TableDataResponse<ComparisonTableRow>> {
    return defer(() => {
      const normalized = this.normalizeQuery(query);
      const afterSearch = this.applySearch(
        this.comparisonTableRows,
        normalized.search
      );
      const afterSingleFilters = this.applyColumnFilters(
        afterSearch,
        normalized.filters
      );
      const afterMultiFilters = this.applyMultiFilters(
        afterSingleFilters,
        normalized.multiFilters
      );
      const sorted = this.applySort(
        afterMultiFilters,
        normalized.sortField,
        normalized.sortOrder
      );
      const start = normalized.page * normalized.rows;
      const pageData = sorted.slice(start, start + normalized.rows);
      return of({
        data: pageData,
        totalRecords: sorted.length,
      } satisfies TableDataResponse<ComparisonTableRow>);
    }).pipe(delay(300));
  }

  getFeatureLabel(featureId: string): string {
    const num = featureId.replace(/^feature/i, '') || '1';
    return `Feature ${num}`;
  }

  getLineChartConfig(
    featureIds: string[],
    options?: { dateRange?: { start: Date; end: Date }; metricLabel?: string }
  ): LineChartConfig | null {
    if (featureIds.length === 0) return null;
    const range = options?.dateRange;
    const axisCategories = [...X_AXIS_CATEGORIES];
    const pointCount = axisCategories.length;
    const dateLabels =
      range && pointCount > 0
        ? dateLabelsForRange(range.start, range.end, pointCount)
        : null;
    const series: LineSeries[] = featureIds.map((id, i) => {
      const color = FEATURE_LINE_COLORS[i % FEATURE_LINE_COLORS.length];
      const data = this.generateLineSeriesData(i, pointCount);
      return {
        name: this.getFeatureLabel(id),
        data,
        smooth: true,
        showSymbol: true,
        symbolSize: 6,
        color,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: color + '99' },
              { offset: 1, color: color + '00' },
            ],
          },
        },
      };
    });
    const splitLineStyle = {
      show: true,
      lineStyle: {
        color: 'rgba(148, 163, 184, 0.18)',
        type: 'dotted' as const,
      },
    };
    const metricLabel = options?.metricLabel ?? 'Total Count';
    return {
      type: 'line',
      theme: 'dark',
      legend: {
        show: true,
        left: '82%',
        top: 'middle',
        orient: 'vertical',
        align: 'left',
        itemGap: 22,
        itemWidth: 12,
        itemHeight: 12,
        icon: 'circle',
        itemStyle: { borderWidth: 0, margin: 12 },
        textStyle: { color: '#FFFFFF', fontSize: 17, fontWeight: 500 },
        formatter: (name: string) => this.wrapLegendName(name, 18),
      },
      grid: {
        left: '3%',
        right: '18%',
        top: 40,
        bottom: 50,
        containLabel: true,
        backgroundColor: '#1A202C',
      },
      tooltip: {
        trigger: 'axis' as const,
        formatter: dateLabels
          ? (params: unknown) =>
              this.formatTooltipWithDates(params as Array<{ dataIndex: number; seriesName: string; value: number }>, dateLabels)
          : undefined,
      },
      xAxis: {
        type: 'category',
        name: 'Time (MM/DD/YY)',
        data: axisCategories,
        boundaryGap: false,
        splitLine: splitLineStyle,
        axisLabel: { color: '#E2E8F0', margin: 12 },
      },
      yAxis: {
        type: 'value',
        name: metricLabel,
        nameRotate: 90,
        nameLocation: 'middle',
        nameGap: 24,
        nameTextStyle: { color: '#E2E8F0', fontSize: 12 },
        min: 0,
        max: 80,
        interval: 20,
        splitLine: splitLineStyle,
        axisLabel: { color: '#E2E8F0', margin: 1 },
      },
      series,
    } satisfies LineChartConfig;
  }

  /** ECharts axis tooltip formatter: show date for the hovered index, then series names and values. */
  private formatTooltipWithDates(
    params: Array<{ dataIndex: number; seriesName: string; value: number }>,
    dateLabels: string[]
  ): string {
    if (!params?.length) return '';
    const dataIndex = params[0].dataIndex;
    const date = dateLabels[dataIndex] ?? `D${dataIndex + 1}`;
    const lines = [date, ...params.map((p) => `${p.seriesName}: ${p.value}`)];
    return lines.join('<br/>');
  }

  /** Wraps long legend names at word boundaries (or breaks long words) so they don't overlap the chart. */
  private wrapLegendName(name: string, maxCharsPerLine: number): string {
    if (name.length <= maxCharsPerLine) return name;
    const words = name.split(/\s+/);
    const lines: string[] = [];
    let current = '';
    for (const w of words) {
      const next = current ? current + ' ' + w : w;
      if (next.length <= maxCharsPerLine) {
        current = next;
      } else {
        if (current) lines.push(current);
        if (w.length > maxCharsPerLine) {
          for (let i = 0; i < w.length; i += maxCharsPerLine) {
            lines.push(w.slice(i, i + maxCharsPerLine));
          }
          current = '';
        } else {
          current = w;
        }
      }
    }
    if (current) lines.push(current);
    return lines.join('\n');
  }

  private generateLineSeriesData(seriesIndex: number, pointCount: number): number[] {
    const base = 20 + seriesIndex * 8;
    const amplitude = 15 + seriesIndex * 5;
    const n = Math.max(1, pointCount);
    return Array.from({ length: n }, (_, i) => {
      const wave = Math.sin((i / n) * Math.PI * 2) * amplitude;
      return Math.round(Math.max(0, Math.min(80, base + wave + (i % 3) * 5)));
    });
  }

  private normalizeQuery(
    q: TableQuery
  ): Required<Pick<TableQuery, 'page' | 'rows'>> & TableQuery {
    return {
      ...q,
      page: Math.max(0, q.page ?? 0),
      rows: Math.max(1, q.rows ?? 10),
    };
  }

  private applySearch(
    rows: readonly ComparisonTableRow[],
    search: string | undefined
  ): ComparisonTableRow[] {
    const term = (search ?? '').trim().toLowerCase();
    if (!term) return [...rows];
    return rows.filter(
      (r) =>
        String(r.medDescription).toLowerCase().includes(term) ||
        String(r.removalDate).toLowerCase().includes(term) ||
        String(r.removalTime).toLowerCase().includes(term) ||
        String(r.employeeName).toLowerCase().includes(term) ||
        String(r.employeeId).toLowerCase().includes(term) ||
        String(r.jobRole).toLowerCase().includes(term) ||
        String(r.patientName).toLowerCase().includes(term) ||
        String(r.patientId).toLowerCase().includes(term) ||
        String(r.clinicalUnit).toLowerCase().includes(term)
    );
  }

  private applyColumnFilters(
    rows: readonly ComparisonTableRow[],
    filters: Record<string, string> | undefined
  ): ComparisonTableRow[] {
    if (!filters || Object.keys(filters).length === 0) return [...rows];
    return rows.filter((row) => {
      return Object.entries(filters).every(([field, filterValue]) => {
        if (!(field in row)) return true;
        const term = (filterValue ?? '').trim().toLowerCase();
        if (!term) return true;
        const cell = row[field as keyof ComparisonTableRow];
        const cellStr = cell != null ? String(cell).toLowerCase() : '';
        return cellStr.includes(term);
      });
    });
  }

  /** Filters rows by multi-value filters: row is kept if row[field] is in the allowed values list. */
  private applyMultiFilters(
    rows: readonly ComparisonTableRow[],
    multiFilters: Record<string, string[]> | undefined
  ): ComparisonTableRow[] {
    if (!multiFilters || Object.keys(multiFilters).length === 0) return [...rows];
    const normalized = Object.fromEntries(
      Object.entries(multiFilters).map(([k, v]) => [
        k,
        new Set((v ?? []).map((s) => String(s).trim().toLowerCase()).filter(Boolean)),
      ])
    );
    return rows.filter((row) => {
      return Object.entries(normalized).every(([field, allowedSet]) => {
        if (!(field in row)) return true;
        if (allowedSet.size === 0) return true;
        const cell = row[field as keyof ComparisonTableRow];
        const cellStr = cell != null ? String(cell).trim().toLowerCase() : '';
        return allowedSet.has(cellStr) || [...allowedSet].some((allowed) => cellStr.includes(allowed));
      });
    });
  }

  private applySort(
    rows: ComparisonTableRow[],
    sortField: string | undefined,
    sortOrder: 1 | -1 | undefined
  ): ComparisonTableRow[] {
    if (!sortField) return rows;
    const order: 1 | -1 = sortOrder === -1 ? -1 : 1;
    return [...rows].sort((a, b) => {
      const av = a[sortField as keyof ComparisonTableRow];
      const bv = b[sortField as keyof ComparisonTableRow];
      if (av == null && bv == null) return 0;
      if (av == null) return -1 * order;
      if (bv == null) return 1 * order;
      if (typeof av === 'number' && typeof bv === 'number')
        return (av - bv) * order;
      return String(av).localeCompare(String(bv)) * order;
    });
  }

  private generateComparisonTableRows(count: number): ComparisonTableRow[] {
    const medications: readonly string[] = [
      'lorazepam (ATIVAN) 2 mg Tab',
      'lorazepam Inj (ATIVAN) 2 mg (1 mL) vial',
      'FENTANYL (SUBLIMAZE) 100 mcg (2 mL) vial',
      'propofol (DIPRIVAN) 1000 mg (100 mL) Bottle',
      'Buprenorphine HCl Sublingual (SUBUTEX) 8 mg Tab',
      'propofol (DIPRIVAN) 200 mg (20 mL) Bottle',
    ];
    const dates: readonly string[] = Array.from({ length: 31 }, (_, i) => {
      const d = i + 1;
      return `2024-01-${d < 10 ? '0' + d : d}`;
    });
    const times: readonly string[] = [
      '08:00 AM',
      '09:00 AM',
      '10:00 AM',
      '11:00 AM',
      '12:00 PM',
      '01:00 PM',
      '02:00 PM',
      '03:00 PM',
      '04:00 PM',
      '05:00 PM',
      '06:00 PM',
      '07:00 PM',
    ];
    const employeeNames: readonly string[] = [
      'John Doe',
      'Jane Smith',
      'Maria Garcia',
      'James Wilson',
      'Sarah Johnson',
      'Robert Brown',
      'Emily Davis',
      'Michael Martinez',
      'Lisa Anderson',
      'David Taylor',
    ];
    const employeeIds: readonly string[] = [
      'LIMO928',
      'KLWE345',
      'ERSC543',
      'NAWE128',
      'LADI987',
      'GRRI093',
    ];
    const jobRoles: readonly string[] = [
      'Nurse',
      'Contract Nurse',
      'CRNA',
    ];
    const patientNames: readonly string[] = [
      'Alice Cooper',
      'Bob Miller',
      'Carol White',
      'Daniel Lee',
      'Eve Harris',
      'Frank Clark',
      'Grace Lewis',
      'Henry Walker',
      'Ivy Hall',
      'Jack Young',
    ];
    const patientIds: readonly string[] = [
      'NMZX1001',
      'NMZX1002',
      'NMZX1003',
      'NMZX1004',
      'NMZX1005',
      'NMZX1006',
      'NMZX1007',
      'NMZX1008',
      'NMZX1009',
      'NMZX1010',
    ];
    const clinicalUnits: readonly string[] = [
      'H-Green',
      'G-Neuro/Surg',
      'LD Anesthesia',
      'Carh Holding Area',
      'CTICU',
      'Acute Care-2',
    ];

    return Array.from({ length: count }, (_, i) => ({
      incident: 10000 + i + 1,
      medDescription: medications[i % medications.length],
      removalDate: dates[i % dates.length],
      removalTime: times[i % times.length],
      employeeName: employeeNames[i % employeeNames.length],
      employeeId: employeeIds[i % employeeIds.length],
      jobRole: jobRoles[i % jobRoles.length],
      patientName: patientNames[i % patientNames.length],
      patientId: patientIds[i % patientIds.length],
      clinicalUnit: clinicalUnits[i % clinicalUnits.length],
    }));
  }
}
